import React, { useEffect, useRef, useState } from 'react';
import { X, Printer, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { DepositReceipt } from './DepositReceipt';
import { RefundReceipt } from './RefundReceipt';
import { BookingInvoice } from './BookingInvoice';
import { LateCheckoutReceipt } from './LateCheckoutReceipt';
import { useTranslation } from 'react-i18next';
import { useApi } from '../Store/ApiContext';

export const PrintReceiptModal = ({
  isOpen,
  onClose,
  type,
  request,
  deposit,
  refund,
  lateCheckout,
  isRTL = false,
}) => {

  const { getData } = useApi();
  const { t } = useTranslation();
  const printRef = useRef(null);

  const [organizationInvoiceData, setOrganizationInvoiceData] = useState(null);

  useEffect(() => {
    getData(setOrganizationInvoiceData, "organization");
  }, []);

  if (!isOpen) return null;

  const generateReceiptNumber = () => {
    const prefix = type === 'deposit' ? 'DEP' :
      type === 'refund' ? 'REF' :
        type === 'late-checkout' ? 'LATE' : 'INV';

    const payment = type === 'deposit' ? deposit :
      type === 'refund' ? refund : null;

    // determine index for deposit based on matching payment inside request.reqPayments
    let index = 0;
    try {
      const payments = request?.deposits ?? [];
      if (payments.length) {
        // No paymentId provided, try matching by type, amount and paymentMethod as fallback
        const idxByAmount = payments.findIndex(p =>
          (p.paymentType === payment?.paymentType && p.amountPaid === payment?.amountPaid && p.paymentMethod === payment?.paymentMethod && p.referenceNumber === payment?.referenceNumber)
        );
        index = idxByAmount;
      }
    } catch (e) {
      console.log("error generating receipt number", e);
      // keep index = 0 on any error
      index = 0;
    }
    return `${prefix}-${request?.reqCode ?? 'UNKNOWN'}-${index + 1}`;
  };

  const handlePrint = () => {
    if (printRef.current) {
      // Store original content
      const originalContents = document.body.innerHTML;
      const originalTitle = document.title;

      // Create print styles
      const printStyles = document.createElement('style');
      printStyles.innerHTML = `
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            font-family: 'Courier New', monospace;
            background: white;
            color: black;
            line-height: 1.4;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .bg-white { background: white !important; }
          .text-gray-900 { color: #111827 !important; }
          .text-gray-800 { color: #1f2937 !important; }
          .text-gray-700 { color: #374151 !important; }
          .text-gray-600 { color: #4b5563 !important; }
          .text-gray-500 { color: #6b7280 !important; }
          .text-gray-400 { color: #9ca3af !important; }
          .border-gray-300 { border-color: #d1d5db !important; }
          .border-gray-200 { border-color: #e5e7eb !important; }
          .border-dashed { border-style: dashed !important; }
          .border-t { border-top-width: 1px !important; }
          .border-b { border-bottom-width: 1px !important; }
          .border-t-2 { border-top-width: 2px !important; }
          .border-b-2 { border-bottom-width: 2px !important; }
          .font-mono { font-family: 'Courier New', monospace !important; }
          .font-bold { font-weight: bold !important; }
          .font-semibold { font-weight: 600 !important; }
          .font-medium { font-weight: 500 !important; }
          .text-sm { font-size: 0.875rem !important; }
          .text-xs { font-size: 0.75rem !important; }
          .text-lg { font-size: 1.125rem !important; }
          .text-xl { font-size: 1.25rem !important; }
          .text-2xl { font-size: 1.5rem !important; }
          .mb-1 { margin-bottom: 0.25rem !important; }
          .mb-2 { margin-bottom: 0.5rem !important; }
          .mb-3 { margin-bottom: 0.75rem !important; }
          .mb-4 { margin-bottom: 1rem !important; }
          .mb-6 { margin-bottom: 1.5rem !important; }
          .mt-1 { margin-top: 0.25rem !important; }
          .mt-2 { margin-top: 0.5rem !important; }
          .mt-4 { margin-top: 1rem !important; }
          .p-2 { padding: 0.5rem !important; }
          .p-3 { padding: 0.75rem !important; }
          .p-4 { padding: 1rem !important; }
          .p-8 { padding: 2rem !important; }
          .pt-2 { padding-top: 0.5rem !important; }
          .pt-3 { padding-top: 0.75rem !important; }
          .pt-4 { padding-top: 1rem !important; }
          .pb-1 { padding-bottom: 0.25rem !important; }
          .pb-4 { padding-bottom: 1rem !important; }
          .pb-6 { padding-bottom: 1.5rem !important; }
          .px-2 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
          .text-center { text-align: center !important; }
          .text-right { text-align: right !important; }
          .flex { display: flex !important; }
          .justify-between { justify-content: space-between !important; }
          .justify-center { justify-content: center !important; }
          .items-center { align-items: center !important; }
          .space-y-1 > * + * { margin-top: 0.25rem !important; }
          .space-y-2 > * + * { margin-top: 0.5rem !important; }
          .space-y-6 > * + * { margin-top: 1.5rem !important; }
          .max-w-md { max-width: 28rem !important; }
          .max-w-2xl { max-width: 42rem !important; }
          .mx-auto { margin-left: auto !important; margin-right: auto !important; }
          .rounded { border-radius: 0.25rem !important; }
          .rounded-full { border-radius: 9999px !important; }
          .bg-gray-50 { background-color: #f9fafb !important; }
          .bg-green-100 { background-color: #dcfce7 !important; }
          .text-green-800 { color: #166534 !important; }
          .text-yellow-800 { color: #92400e !important; }
          .bg-yellow-100 { background-color: #fef3c7 !important; }
          .bg-red-50 { background-color: #fef2f2 !important; }
          .bg-orange-50 { background-color: #fff7ed !important; }
          .bg-blue-50 { background-color: #eff6ff !important; }
          .text-red-600 { color: #dc2626 !important; }
          .text-orange-600 { color: #ea580c !important; }
          .text-blue-600 { color: #2563eb !important; }
          .grid { display: grid !important; }
          .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
          .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          .gap-4 { gap: 1rem !important; }
          .gap-8 { gap: 2rem !important; }
        }
        @page {
          margin: 0.5in;
          size: A4;
        }
      `;

      // Add print styles to head
      document.head.appendChild(printStyles);

      // Add print class to the receipt content
      const printContent = printRef.current.cloneNode(true);
      printContent.classList.add('print-content');

      // Temporarily add the print content to body
      document.body.appendChild(printContent);

      // Set document title for print
      document.title = type === 'deposit' ? 'Deposit Receipt' : type === 'refund' ? 'Refund Receipt' : 'Booking Invoice';

      // Print
      window.print();

      // Clean up - remove print styles and content
      document.head.removeChild(printStyles);
      document.body.removeChild(printContent);
      document.title = originalTitle;
    }
  };

  const handleDownload = () => {
    if (printRef.current) {
      // Temporarily hide elements that should be excluded from PDF
      const pdfHiddenEls = Array.from(document.querySelectorAll('.pdf-hidden'));
      const originalDisplay = pdfHiddenEls.map(el => el.style.display);
      pdfHiddenEls.forEach(el => { el.style.display = 'none'; });
      // Generate PDF from the receipt content
      html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: printRef.current.scrollWidth,
        height: printRef.current.scrollHeight
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Calculate dimensions to fit the content properly
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        // Add first page
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add additional pages if content is longer than one page
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // Generate filename based on type
        const filename = type === 'deposit'
          ? `deposit-receipt-${request.reqCode}-${Date.now()}.pdf`
          : type === 'refund'
            ? `refund-receipt-${request.reqCode}-${Date.now()}.pdf`
            : `booking-invoice-${request.reqCode}-${Date.now()}.pdf`;

        pdf.save(filename);
        // Restore hidden elements after PDF generation
        pdfHiddenEls.forEach((el, idx) => { el.style.display = originalDisplay[idx] || ''; });
      }).catch(error => {
        // Restore hidden elements if PDF generation fails
        const pdfHiddenElsCatch = Array.from(document.querySelectorAll('.pdf-hidden'));
        pdfHiddenElsCatch.forEach((el, idx) => { el.style.display = originalDisplay[idx] || ''; });
        console.error('Error generating PDF:', error);
        // Fallback to HTML download if PDF generation fails
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>${type === 'deposit' ? 'Deposit Receipt' : type === 'refund' ? 'Refund Receipt' : 'Booking Invoice'}</title>
              <meta charset="UTF-8">
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  font-family: 'Courier New', monospace; 
                  background: white;
                }
              </style>
            </head>
            <body>
              ${printRef.current?.innerHTML}
            </body>
          </html>
        `;

        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type}-receipt-${request.reqCode}-${Date.now()}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    }
  };

  const receiptNumber = generateReceiptNumber();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                {type === 'deposit'
                  ? t('printDepositReceipt')
                  : type === 'refund'
                    ? t('printRefundReceipt')
                    : t('printBookingInvoice')
                }
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t('requestNumberWithId', { id: request.reqCode })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Receipt Preview */}
        <div className="p-6">
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div ref={printRef}>
              {type === 'deposit' && deposit ? (
                <DepositReceipt
                  organizationInvoiceData={organizationInvoiceData}
                  request={request}
                  deposit={deposit}
                  receiptNumber={receiptNumber}
                />
              ) : type === 'refund' && refund ? (
                <RefundReceipt
                  organizationInvoiceData={organizationInvoiceData}
                  request={request}
                  refund={refund}
                  receiptNumber={receiptNumber}
                />
              ) : type === 'late-checkout' && lateCheckout ? (
                <LateCheckoutReceipt
                  organizationInvoiceData={organizationInvoiceData}
                  request={request}
                  feeAmount={lateCheckout.feeAmount}
                  hoursLate={lateCheckout.hoursLate}
                  receiptNumber={receiptNumber}
                  reason={lateCheckout.reason}
                  hourlyRate={lateCheckout.hourlyRate}
                  gracePeriodMinutes={lateCheckout.gracePeriodMinutes}
                />
              ) : type === 'invoice' ? (
                <BookingInvoice
                  organizationInvoiceData={organizationInvoiceData}
                  request={request}
                  invoiceNumber={receiptNumber}
                />
              ) : null}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex space-x-reverse space-x-3`}>
            <button
              onClick={handlePrint}
              className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-1 sm:px-4 rounded-xl transition-colors duration-200 flex items-center justify-center`}
            >
              <Printer size={20} />
              <span>{t('print')}</span>
            </button>

            <button
              onClick={handleDownload}
              className={`flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-1 sm:px-4 rounded-xl transition-colors duration-200 flex items-center justify-center`}
            >
              <Download size={20} />
              <span>{t('downloadPdf')}</span>
            </button>

            <button
              onClick={onClose}
              className={`flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-1 sm:px-4 rounded-xl transition-colors duration-200 flex items-center justify-center`}
            >
              <X size={20} />
              <span>{t('close')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};